/* Admin wiki. Content: wiki/wikiContent.js (English) + wiki.<id>.title/.summary/.content in every
 * locale. Layout: grouped side menu with search · guide with "On this page" · prev/next.
 * The open guide is in the URL hash (#map-replay), so a guide can be linked to. */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WIKI_GROUPS, WIKI_GUIDES } from './wiki/wikiContent';
import './wiki/wiki.css';

/* ── icons ── */
const P = {
  rocket: <><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" /><path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" /></>,
  book: <><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /></>,
  swords: <><path d="M14.5 17.5L3 6V3h3l11.5 11.5" /><path d="M13 19l6-6M16 16l4 4M19 21l2-2" /></>,
  check: <><circle cx="12" cy="12" r="9" /><path d="M8 12l3 3 5-6" /></>,
  terminal: <><polyline points="4 17 10 11 4 5" /><line x1="12" y1="19" x2="20" y2="19" /></>,
  sliders: <><path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6" /></>,
  replay: <><path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" /><polygon points="10 9 15 12 10 15 10 9" /></>,
  drive: <><rect x="2" y="12" width="20" height="8" rx="2" /><path d="M5.5 12L8 4h8l2.5 8M6 16h.01M10 16h.01" /></>,
  map: <><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" /><path d="M8 2v16M16 6v16" /></>,
  key: <><circle cx="7.5" cy="15.5" r="5.5" /><path d="M21 2l-9.6 9.6M15.5 7.5l3 3L22 7l-3-3" /></>,
  users: <><circle cx="9" cy="8" r="4" /><path d="M2 21a7 7 0 0 1 14 0M16 3.1a4 4 0 0 1 0 7.8M22 21a7 7 0 0 0-4-6.3" /></>,
  clipboard: <><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /><rect x="8" y="2" width="8" height="4" rx="1" /></>,
  trophy: <><path d="M6 9H4a2 2 0 0 1 0-4h2M18 9h2a2 2 0 0 0 0-4h-2M4 22h16M10 14.7V17c0 .6-.5 1-1 1.2C7.8 18.8 7 20.2 7 22M14 14.7V17c0 .6.5 1 1 1.2 1.2.6 2 2 2 3.8" /><path d="M18 2H6v7a6 6 0 0 0 12 0V2z" /></>,
  chart: <><path d="M3 3v18h18" /><path d="M7 16v-5M12 16V8M17 16v-8" /></>,
  globe: <><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></>,
  server: <><rect x="3" y="4" width="18" height="7" rx="2" /><rect x="3" y="13" width="18" height="7" rx="2" /><path d="M7 7.5h.01M7 16.5h.01" /></>,
  folder: <><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" /></>,
  network: <><rect x="9" y="2" width="6" height="6" rx="1" /><rect x="2" y="16" width="6" height="6" rx="1" /><rect x="16" y="16" width="6" height="6" rx="1" /><path d="M12 8v4M5 16v-4h14v4" /></>,
  shield: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></>,
  mic: <><rect x="9" y="2" width="6" height="12" rx="3" /><path d="M19 10v1a7 7 0 0 1-14 0v-1M12 18v4" /></>,
  star: <><polygon points="12 2 15.1 8.3 22 9.3 17 14.1 18.2 21 12 17.8 5.8 21 7 14.1 2 9.3 8.9 8.3 12 2" /></>,
  code: <><polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" /></>,
  search: <><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></>,
};
const Icon = ({ name, size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{P[name] || P.book}</svg>
);

/* ── tiny markdown: **bold**, `code` ── */
function inline(text, keyBase) {
  return String(text).split(/(\*\*[^*]+\*\*|`[^`]+`)/g).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={`${keyBase}-${i}`}>{part.slice(2, -2)}</strong>;
    if (part.startsWith('`') && part.endsWith('`')) return <code key={`${keyBase}-${i}`}>{part.slice(1, -1)}</code>;
    return <React.Fragment key={`${keyBase}-${i}`}>{part}</React.Fragment>;
  });
}

const slug = (s) => String(s).toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-|-$/g, '') || 'section';

/** content string → [{ title, id, blocks }]; text before the first ## goes in an untitled section */
function parse(content) {
  const sections = [];
  let cur = { title: null, id: 'intro', blocks: [] };
  const push = (type, item) => {
    const last = cur.blocks[cur.blocks.length - 1];
    if (last && last.type === type && (type === 'ul' || type === 'ol')) last.items.push(item);
    else cur.blocks.push({ type, items: [item] });
  };
  for (const raw of String(content || '').split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    if (line.startsWith('## ')) {
      if (cur.title || cur.blocks.length) sections.push(cur);
      const title = line.slice(3).trim();
      cur = { title, id: slug(title), blocks: [] };
    } else if (/^>\s*(tip|warn):/i.test(line)) {
      const m = line.match(/^>\s*(tip|warn):\s*(.*)$/i);
      cur.blocks.push({ type: m[1].toLowerCase(), items: [m[2]] });
    } else if (/^[-•]\s/.test(line)) push('ul', line.replace(/^[-•]\s+/, ''));
    else if (/^\d+\.\s/.test(line)) push('ol', line.replace(/^\d+\.\s+/, ''));
    else cur.blocks.push({ type: 'p', items: [line] });
  }
  if (cur.title || cur.blocks.length) sections.push(cur);
  return sections;
}

function Blocks({ blocks, keyBase }) {
  const { t } = useTranslation();
  return blocks.map((b, i) => {
    const k = `${keyBase}-${i}`;
    if (b.type === 'ul') return <ul key={k}>{b.items.map((it, j) => <li key={j}>{inline(it, `${k}-${j}`)}</li>)}</ul>;
    if (b.type === 'ol') return <ol key={k}>{b.items.map((it, j) => <li key={j}><span className="wk-step">{j + 1}</span><span>{inline(it, `${k}-${j}`)}</span></li>)}</ol>;
    if (b.type === 'tip' || b.type === 'warn') {
      return (
        <div key={k} className={`wk-callout ${b.type}`}>
          <span className="wk-callout-title">{b.type === 'tip' ? t('wiki.ui.tip', 'Tip') : t('wiki.ui.warn', 'Important')}</span>
          <span>{inline(b.items[0], k)}</span>
        </div>
      );
    }
    return <p key={k}>{inline(b.items[0], k)}</p>;
  });
}

// smooth scrolling only for people who haven't asked for less motion
const scrollBehavior = () => (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth');

const DEFAULT_ID = WIKI_GUIDES[0].id;
const idFromHash = () => {
  const h = decodeURIComponent((window.location.hash || '').replace(/^#/, ''));
  return WIKI_GUIDES.some(g => g.id === h) ? h : DEFAULT_ID;
};

function Wiki() {
  const { t } = useTranslation();
  const [activeId, setActiveId] = useState(idFromHash);
  const [query, setQuery] = useState('');
  const articleRef = useRef(null);

  const T = useMemo(() => {
    const map = {};
    for (const g of WIKI_GUIDES) {
      map[g.id] = {
        title: t(`wiki.${g.id}.title`, g.title),
        summary: t(`wiki.${g.id}.summary`, g.summary),
        content: t(`wiki.${g.id}.content`, g.content),
      };
    }
    return map;
  }, [t]);

  useEffect(() => {
    const onHash = () => setActiveId(idFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const open = (id) => {
    setActiveId(id);
    setQuery('');
    if (window.location.hash !== `#${id}`) window.history.replaceState(null, '', `#${id}`);
    if (articleRef.current) articleRef.current.scrollIntoView({ block: 'start', behavior: scrollBehavior() });
  };

  // search: title + summary + content of the current language
  const q = query.trim().toLowerCase();
  const results = useMemo(() => {
    if (!q) return null;
    return WIKI_GUIDES.map(g => {
      const tr = T[g.id];
      const hay = `${tr.title}\n${tr.summary}\n${tr.content}`.replace(/[*`#>]/g, '');
      const at = hay.toLowerCase().indexOf(q);
      if (at < 0) return null;
      const start = Math.max(0, at - 40);
      const snippet = (start ? '…' : '') + hay.slice(start, at + q.length + 70).replace(/\s+/g, ' ') + '…';
      return { g, snippet, inTitle: tr.title.toLowerCase().includes(q) };
    }).filter(Boolean).sort((a, b) => Number(b.inTitle) - Number(a.inTitle));
  }, [q, T]);

  const guide = WIKI_GUIDES.find(g => g.id === activeId) || WIKI_GUIDES[0];
  const tr = T[guide.id];
  const sections = useMemo(() => parse(tr.content), [tr.content]);
  const idx = WIKI_GUIDES.findIndex(g => g.id === guide.id);
  const prev = WIKI_GUIDES[idx - 1], next = WIKI_GUIDES[idx + 1];
  const groupTitle = (id) => t(`wiki.group.${id}`, (WIKI_GROUPS.find(x => x.id === id) || {}).title || id);

  return (
    <div className="wk">
      <aside className="wk-nav">
        <div className="wk-nav-head"><Icon name="book" size={17} /><span>{t('nav.wiki', 'Wiki')}</span></div>
        <label className="wk-search">
          <Icon name="search" size={14} />
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder={t('wiki.ui.search', 'Search the wiki…')} aria-label={t('wiki.ui.search', 'Search the wiki…')} />
          {query && <button type="button" onClick={() => setQuery('')} aria-label={t('wiki.ui.clear', 'Clear')}>×</button>}
        </label>

        {results ? (
          <div className="wk-results">
            {results.length === 0 && <div className="wk-empty">{t('wiki.ui.noResults', 'Nothing found.')}</div>}
            {results.map(({ g, snippet }) => (
              <button key={g.id} type="button" className="wk-result" onClick={() => open(g.id)}>
                <span className="wk-result-title"><Icon name={g.icon} size={13} />{T[g.id].title}</span>
                <span className="wk-result-snip">{snippet}</span>
              </button>
            ))}
          </div>
        ) : (
          WIKI_GROUPS.map(grp => (
            <div key={grp.id} className="wk-group">
              <div className="wk-group-title">{groupTitle(grp.id)}</div>
              {WIKI_GUIDES.filter(g => g.group === grp.id).map(g => (
                <button key={g.id} type="button" className={`wk-link${g.id === guide.id ? ' on' : ''}`} onClick={() => open(g.id)} aria-current={g.id === guide.id ? 'page' : undefined}>
                  <Icon name={g.icon} size={14} /><span>{T[g.id].title}</span>
                </button>
              ))}
            </div>
          ))
        )}
      </aside>

      <article className="wk-article" ref={articleRef}>
        <div className="wk-crumb">{groupTitle(guide.group)}</div>
        <h1 className="wk-title">{tr.title}</h1>
        <p className="wk-summary">{inline(tr.summary, 'sum')}</p>

        {sections.filter(s => s.title).length > 1 && (
          <nav className="wk-toc" aria-label={t('wiki.ui.onThisPage', 'On this page')}>
            <span className="wk-toc-title">{t('wiki.ui.onThisPage', 'On this page')}</span>
            {sections.filter(s => s.title).map(s => (
              <a key={s.id} href={`#${guide.id}`} onClick={e => { e.preventDefault(); const el = document.getElementById(`wk-${guide.id}-${s.id}`); if (el) el.scrollIntoView({ behavior: scrollBehavior(), block: 'start' }); }}>{s.title}</a>
            ))}
          </nav>
        )}

        {sections.map(s => (
          <section key={s.id} className="wk-section" id={`wk-${guide.id}-${s.id}`}>
            {s.title && <h2>{s.title}</h2>}
            <Blocks blocks={s.blocks} keyBase={s.id} />
          </section>
        ))}

        <div className="wk-pager">
          {prev ? <button type="button" onClick={() => open(prev.id)}><span>‹ {t('wiki.ui.previous', 'Previous')}</span><b>{T[prev.id].title}</b></button> : <span />}
          {next ? <button type="button" className="next" onClick={() => open(next.id)}><span>{t('wiki.ui.next', 'Next')} ›</span><b>{T[next.id].title}</b></button> : <span />}
        </div>
      </article>
    </div>
  );
}

export default Wiki;
